import dayjs from "dayjs";
import { useState, useCallback, useEffect } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import { DEFAULT_DATE_PICKER } from "../constant";
import { useSearchParams } from "react-router-dom";

const parseDateParam = (value: string | null) => {
  if (!value) {
    return dayjs("");
  }

  const displayDateMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (displayDateMatch) {
    const [, day, month, year] = displayDateMatch;
    return dayjs(`${year}-${month}-${day}`);
  }

  return dayjs(value);
};

const getDateRangeFromSearchParams = (searchParams: URLSearchParams) => {
  const startDate = parseDateParam(searchParams.get("startDate"));
  const endDate = parseDateParam(searchParams.get("endDate"));

  if (!startDate.isValid() || !endDate.isValid()) {
    return undefined;
  }

  return {
    startDate: startDate.startOf("day").toDate(),
    endDate: endDate.endOf("day").toDate(),
  };
};

const useDatePicker = (defaultDate?: { startDate: Date; endDate: Date }) => {
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(
    () => getDateRangeFromSearchParams(searchParams) ?? defaultDate ?? DEFAULT_DATE_PICKER
  );
  const handleChange = useCallback((newValue: DateValueType) => {
    if (newValue && newValue.startDate && newValue.endDate) {
      setValue({
        startDate: dayjs(newValue.startDate).startOf("day").toDate(),
        endDate: dayjs(newValue.endDate).endOf("day").toDate(),
      });
    } else {
      setValue(DEFAULT_DATE_PICKER);
    }
  }, []);

  useEffect(() => {
    const dateRange = getDateRangeFromSearchParams(searchParams);
    if (dateRange) {
      setValue(dateRange);
    }
  }, [searchParams]);

  return {
    handleChange,
    value,
  };
};

export default useDatePicker;
